import type { ErrorReportArgs, StageReportArgs, UserActionArgs } from '@itero/observability';
import { BiAction } from '@core/services/logger/interfaces/bi.enums';

/**
 * BI events for the Custom Rx (Dentira) integration.
 * Contract: https://aligntech.atlassian.net/wiki/spaces/ITEROWEBRESTO/pages/369308217/Dentira+Rx+-+BI+Events
 */
export const CUSTOM_RX_BI_FEATURE = 'Custom Rx';

export enum CustomRxBiObject {
	ContinueButton = 'Custom Rx Continue Button',
	Ui = 'Custom Rx UI',
	DialogConfirm = 'Custom Rx Dialog Confirm'
}

export enum CustomRxBiDialog {
	SaveAndContinueLater = 'Save And Continue Later Dialog',
	NotResponding = 'Custom Rx Not Responding Dialog'
}

export enum CustomRxBiDialogOption {
	CompleteAndSubmitToPartner = 'Complete And Submit To Partner',
	ContinueOnScannerLater = 'Continue On Scanner Later',
	TryAgainLater = 'Try Again Later'
}

const RX_SUMMARY_LOCATION = 'Rx Summary';
const BUTTON_OBJECT_TYPE = 'Button';

const customAttributes = (partnerName: string) => ({
	feature: CUSTOM_RX_BI_FEATURE,
	partnerName
});

export const buildCustomRxContinueClickEvent = (partnerName: string): UserActionArgs => ({
	objectName: CustomRxBiObject.ContinueButton,
	action: BiAction.Clicked,
	objectType: BUTTON_OBJECT_TYPE,
	locationParentObject: RX_SUMMARY_LOCATION,
	customAttributes: customAttributes(partnerName)
});

export const buildCustomRxUiLoadedEvent = (partnerName: string): StageReportArgs => ({
	object: CustomRxBiObject.Ui,
	stage: 'loaded',
	previous: '',
	trigger: 'system',
	description: `${partnerName} UI sent RxUiReady`,
	customAttributes: customAttributes(partnerName)
});

export const buildCustomRxUiCompletedEvent = (partnerName: string): StageReportArgs => ({
	object: CustomRxBiObject.Ui,
	stage: 'completed',
	previous: 'loaded',
	trigger: 'user',
	description: `${partnerName} sent RxComplete SUCCESS`,
	customAttributes: customAttributes(partnerName)
});

export const buildCustomRxUiNotRespondingEvent = (partnerName: string): ErrorReportArgs => ({
	object: CustomRxBiObject.Ui,
	code: 'failed',
	severity: 'warning',
	description: `RxUiReady not received from ${partnerName} within timeout`,
	customAttributes: customAttributes(partnerName)
});

export const buildCustomRxDialogConfirmEvent = (
	dialog: CustomRxBiDialog,
	option: CustomRxBiDialogOption,
	partnerName: string
): UserActionArgs => ({
	objectName: CustomRxBiObject.DialogConfirm,
	action: BiAction.Clicked,
	objectType: BUTTON_OBJECT_TYPE,
	locationParentObject: dialog,
	selectedValue: option,
	customAttributes: customAttributes(partnerName)
});
